import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Save, Scale, History } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loading } from "@/components/common/Loading";
import { RichTextEditor } from "@/components/forms/RichTextEditor";

const DOCS = [
  { key: "terms", label: "Terms of Service", path: "/terms" },
  { key: "privacy", label: "Privacy Policy", path: "/privacy" },
  { key: "returns", label: "Returns Policy", path: "/returns" },
  { key: "accessibility", label: "Accessibility", path: "/accessibility" },
  { key: "affiliate", label: "Affiliate Policy", path: "/affiliate" },
];

export default function LegalEditor() {
  const [docKey, setDocKey] = useState("terms");
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stampToday, setStampToday] = useState(true);
  const [revisions, setRevisions] = useState([]);

  const loadRevisions = (key) =>
    api.get(`/admin/legal/${key}/revisions`)
      .then(({ data }) => setRevisions(data || []))
      .catch(() => setRevisions([]));

  useEffect(() => {
    setC(null);
    setRevisions([]);
    api.get(`/admin/legal/${docKey}`)
      .then(({ data }) => setC(data))
      .catch(() => toast.error("Could not load this page."));
    loadRevisions(docKey);
  }, [docKey]);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/legal/${docKey}`, {
        title: c.title, last_updated: c.last_updated, body_html: c.body_html,
        stamp_today: stampToday,
      });
      setC(data);
      loadRevisions(docKey);
      toast.success("Saved — live now, no redeploy.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const fmt = (iso) => { try { return format(parseISO(iso), "MMM d, yyyy 'at' h:mm a"); } catch { return iso; } };

  const current = DOCS.find((d) => d.key === docKey) || DOCS[0];

  return (
    <div data-testid="legal-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-[680] text-foreground">
            <Scale className="h-5 w-5 text-primary" /> Legal &amp; policy pages
          </h2>
          <p className="text-sm text-muted-foreground">
            Edit Terms, Privacy, Returns, Accessibility &amp; Affiliate — changes go live instantly, no redeploy.
          </p>
        </div>
        <a href={current.path} target="_blank" rel="noreferrer">
          <Button variant="secondary" data-testid="legal-view-live">View live page</Button>
        </a>
      </div>

      <div className="mt-6 space-y-5">
        <div className="space-y-1.5">
          <Label className="text-sm">Page</Label>
          <Select value={docKey} onValueChange={setDocKey}>
            <SelectTrigger className="w-72" data-testid="legal-page-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOCS.map((d) => (
                <SelectItem key={d.key} value={d.key} data-testid={`legal-option-${d.key}`}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!c ? <Loading /> : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Title</Label>
                <Input value={c.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="legal-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Last updated</Label>
                <Input value={c.last_updated || ""} onChange={(e) => set("last_updated", e.target.value)} placeholder="February 1, 2026" disabled={stampToday} data-testid="legal-updated-input" />
              </div>
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="legal-stamp-toggle" className="text-sm font-medium">Auto-stamp “Last updated” to today on save</Label>
                <p className="text-xs text-muted-foreground">
                  On for material changes. Turn off to keep the current date for typo-only edits.
                </p>
              </div>
              <Switch id="legal-stamp-toggle" checked={stampToday} onCheckedChange={setStampToday} data-testid="legal-stamp-toggle" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Body</Label>
              <RichTextEditor
                value={c.body_html || ""}
                onChange={(v) => set("body_html", v)}
                placeholder="Write the policy…"
                testId="legal-body-editor"
                toolbar={["bold", "italic", "underline", "heading", "bullet", "ordered", "link", "image"]}
                editorClassName="prose-so-spacious"
              />
            </div>
          </>
        )}
      </div>

      {revisions.length > 0 && (
        <div className="mt-8 rounded-lg border border-border" data-testid="legal-revisions">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <History className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-[640] text-foreground">Change history</h3>
            <span className="text-xs text-muted-foreground">— last {revisions.length} save{revisions.length === 1 ? "" : "s"}</span>
          </div>
          <ul className="divide-y divide-border">
            {revisions.map((r) => (
              <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-2.5" data-testid={`legal-revision-${r.id}`}>
                <span className="text-sm text-foreground">
                  {r.editor_name || r.editor_email || "Admin"}
                  <span className="text-muted-foreground"> saved “{r.title}”</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {fmt(r.saved_at)}
                  {r.last_updated ? <span className="ml-2 rounded bg-muted px-1.5 py-0.5">Last updated: {r.last_updated}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="sticky bottom-4 mt-6 flex justify-end">
        <Button size="lg" onClick={save} disabled={saving || !c} className="shadow-lg" data-testid="legal-save-button">
          <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
